/**
 * FacebookManager Component (OWZ-32)
 *
 * Container with tabs that demo the Meta scopes pending App Review.
 * This commit adds the Page Info tab (pages_manage_metadata).
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Info, MessageSquare, MessageCircle } from 'lucide-react';

import FacebookPageInfo from './FacebookPageInfo';
import FacebookComments from './FacebookComments';
import FacebookMessages from './FacebookMessages';

import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  getRememberedPageValue,
  rememberPageValue,
} from '@/components/admin/navigation/adminPlaceMemory';
import { getAdminUser } from '@/components/admin/navigation/sectionAccess';

const TABS = [
  { id: 'info', label: 'Page Info', icon: Info },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'messages', label: 'Messages', icon: MessageCircle },
];
const TAB_IDS = TABS.map((tab) => tab.id);

const TAB_SCOPE = 'social.facebook-manager.tab';

const FacebookManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const hasExplicitTab = TAB_IDS.includes(requestedTab);
  const activeTab = hasExplicitTab
    ? requestedTab
    : getRememberedPageValue(TAB_SCOPE, TAB_IDS, 'info', getAdminUser());

  // Pin a queryless (or invalid) entry to the tab it is showing, so Back and
  // Forward — even across remounts — return to what was on screen rather than
  // whatever was remembered later.
  useEffect(() => {
    if (!hasExplicitTab) setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, hasExplicitTab, setSearchParams]);

  // Remember what is displayed, whether it came from a click, a deep link, or
  // Back/Forward.
  useEffect(() => {
    rememberPageValue(TAB_SCOPE, activeTab, TAB_IDS, getAdminUser());
  }, [activeTab]);

  const handleTabChange = (tab) => setSearchParams({ tab });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const socialTab = getRememberedPageValue(
              'socialmedia',
              ['all', 'draft', 'pending_review', 'approved', 'scheduled', 'published'],
              'all',
              getAdminUser(),
            );
            navigate(`/admin/outreach/social?tab=${encodeURIComponent(socialTab)}`);
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold">Facebook Page Manager</h1>
        <div className="w-20" />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="gap-2">
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="info" className="mt-4">
          <FacebookPageInfo />
        </TabsContent>
        <TabsContent value="comments" className="mt-4">
          <FacebookComments />
        </TabsContent>
        <TabsContent value="messages" className="mt-4">
          <FacebookMessages />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FacebookManager;
